import { useState, useEffect } from 'react';
import styled from 'styled-components';
import { IoRefreshOutline, IoBanOutline, IoChevronDownOutline, IoChevronUpOutline } from 'react-icons/io5';
import { doc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../../../firebase/config';
import { useAuth } from '../../../context/AuthContext';
import { getSettlementSummary } from '../../../services/settlementService';
import { getPartnerDashboardStats } from '../../../services/partnerDashboardService';
import { requestTossPayment, buildOrderId } from '../../../services/tossPaymentService';

const TABS = [
  { key: 'receivable', label: '미수금' },
  { key: 'expected', label: '청구 금액' },
  { key: 'history', label: '결제 내역' },
];

const NOTICES = [
  '고객과의 계약 완료 시 품목별 계약 완료 수수료가 청구됩니다.',
  '청구된 수수료가 납부되지 않을 시, 미수금액으로 표시됩니다.',
  '미수금액을 납부하지 않을 시, DB 구매 기능이 제한됩니다.',
];

const fmt = (n) => `₩${(n || 0).toLocaleString()}`;

const SettlementManage = () => {
  const { userData } = useAuth();
  const [tab, setTab] = useState('receivable');
  const [expected, setExpected] = useState([]);
  const [history, setHistory] = useState([]);
  const [receivables, setReceivables] = useState([]);
  const [pipelineCounts, setPipelineCounts] = useState({ completed: 0, reserved: 0 });
  const [loading, setLoading] = useState(true);

  const load = async () => {
    const partnerId = userData?.uid;
    if (!partnerId) { setLoading(false); return; }
    setLoading(true);
    try {
      const [summary, stats] = await Promise.all([
        getSettlementSummary(partnerId),
        getPartnerDashboardStats(partnerId).catch(() => ({ pipelineCounts: {} })),
      ]);
      setExpected(summary.expected);
      setHistory(summary.history);
      setReceivables(summary.receivables);
      setPipelineCounts({
        completed: Number(stats.pipelineCounts?.completed) || 0,
        reserved: Number(stats.pipelineCounts?.reserved) || 0,
      });
    } catch { /* noop */ }
    setLoading(false);
  };

  useEffect(() => { load(); }, [userData?.uid]);

  const data = tab === 'expected' ? expected : tab === 'history' ? history : receivables;

  const receivableTotal = receivables.reduce((s, r) => s + r.amount, 0);
  // 계약 건수 = partner_assignments 의 완료(completed) + 예약완료(reserved). 결제 여부와 무관.
  const contractCount = pipelineCounts.completed + pipelineCounts.reserved;
  // 계약완료 수수료 미처리건 수 = 미수금(pending) 건수 (= 계약 완료됐지만 아직 수수료 미납)
  const pendingFeeCount = receivables.length;
  const confirming = history.filter((h) => h.status === 'confirming').length;
  const paidTotal = history.filter((h) => h.status === 'completed').reduce((s, h) => s + h.amount, 0);

  const [expanded, setExpanded] = useState({});
  const toggleExpand = (id) => setExpanded((prev) => ({ ...prev, [id]: !prev[id] }));

  // 본사 입력 청구 — 결제하기 (바로 토스 결제창)
  const handlePayAdminRequest = async (item) => {
    if (!userData?.uid) { alert('로그인이 필요합니다.'); return; }
    if (!item?.amount || item.amount < 100) { alert('결제 금액이 올바르지 않습니다.'); return; }
    const orderId = buildOrderId('pr');
    const orderName = item.productName || item.label || '본사 청구 결제';
    localStorage.setItem(`order:${orderId}`, JSON.stringify({
      kind: 'payment_request',
      paymentRequestId: item.id,
      partnerUid: userData.uid,
      amount: item.amount,
    }));
    try {
      await requestTossPayment({
        amount: item.amount,
        orderId,
        orderName,
        customerName: userData.business_name || userData.name || '제휴업체',
        method: '카드',
      });
    } catch (err) {
      localStorage.removeItem(`order:${orderId}`);
      alert(err?.message || '결제창 호출 실패');
    }
  };

  // 본사 입력 청구 — 취소하기
  const handleCancelAdminRequest = async (id) => {
    if (!window.confirm('이 청구건을 취소하시겠습니까?')) return;
    try {
      await updateDoc(doc(db, 'partner_payment_requests', id), {
        status: 'cancelled',
        updated_at: serverTimestamp(),
      });
      load();
    } catch (err) {
      console.error('청구 취소 실패:', err);
      alert('취소에 실패했습니다.');
    }
  };

  const [paying, setPaying] = useState(false);
  const handlePayReceivables = async () => {
    if (paying) return;
    if (!userData?.uid) { alert('로그인이 필요합니다.'); return; }
    if (receivables.length === 0) { alert('미납 미수금이 없습니다.'); return; }
    if (receivableTotal < 100) { alert('결제 금액이 올바르지 않습니다.'); return; }
    setPaying(true);
    const orderId = buildOrderId('rcv');
    localStorage.setItem(`order:${orderId}`, JSON.stringify({
      kind: 'receivable_bulk',
      receivableIds: receivables.map((r) => r.id),
      partnerUid: userData.uid,
      amount: receivableTotal,
    }));
    try {
      await requestTossPayment({
        amount: receivableTotal,
        orderId,
        orderName: `미수금 결제 (${receivables.length}건)`,
        customerName: userData.business_name || userData.name || '제휴업체',
        method: '카드',
      });
    } catch (err) {
      localStorage.removeItem(`order:${orderId}`);
      alert(err?.message || '결제창 호출 실패');
      setPaying(false);
    }
  };

  // 월별 그룹핑 (당월/전월/이전)
  const groupByMonth = (list) => {
    const now = new Date();
    const thisY = now.getFullYear();
    const thisM = now.getMonth();
    const groups = {};
    list.forEach((d) => {
      const m = d.date ? /(\d{4})\.\s*(\d{1,2})/.exec(d.date) : null;
      let key = '이전';
      if (m) {
        const y = Number(m[1]);
        const mo = Number(m[2]) - 1;
        if (y === thisY && mo === thisM) key = '당월';
        else if ((y === thisY && mo === thisM - 1) || (thisM === 0 && y === thisY - 1 && mo === 11)) key = '전월';
      }
      if (!groups[key]) groups[key] = [];
      groups[key].push(d);
    });
    const order = ['당월', '전월', '이전'];
    return order.filter((k) => groups[k]?.length).map((k) => ({ label: k, items: groups[k] }));
  };

  const emptyInfo =
    tab === 'expected' ? { title: '청구 내역이 없습니다.', sub: '예약완료 시 자동으로 청구가 생성됩니다.' }
    : tab === 'history' ? { title: '결제 내역이 없습니다.', sub: '' }
    : { title: '미납 미수금이 없습니다.', sub: '모든 금액이 정산되었습니다.' };

  return (
    <Wrap>
      <PageHeader>
        <div>
          <PageTitle>청구 · 미수금 현황</PageTitle>
          <PageSub>미수금 결제, 본사 요청금액을 결제할 수 있는 페이지입니다.</PageSub>
        </div>
        <RefreshBtn type="button" onClick={load}>
          <IoRefreshOutline size={14} /> 새로고침
        </RefreshBtn>
      </PageHeader>

      {/* 상단 요약 카드 */}
      <SummaryCard>
        <SummaryTop>
          <SummaryInfo>
            <SummaryLabel>미수금 합계</SummaryLabel>
            <SummaryAmount>{fmt(receivableTotal)}</SummaryAmount>
          </SummaryInfo>
          <PayAllBtn type="button" onClick={handlePayReceivables} disabled={paying || receivables.length === 0}>
            {paying ? '결제창 여는 중…' : '일괄 결제 →'}
          </PayAllBtn>
        </SummaryTop>
        <KpiGrid>
          <KpiItem>
            <KpiLabel>당월 전체 계약 완료 수</KpiLabel>
            <KpiValue>{contractCount}건</KpiValue>
          </KpiItem>
          <KpiItem>
            <KpiLabel>계약완료 수수료 미처리건 수</KpiLabel>
            <KpiValue>{pendingFeeCount}건</KpiValue>
          </KpiItem>
          <KpiItem>
            <KpiLabel>입금 확인중</KpiLabel>
            <KpiValue>{confirming}건</KpiValue>
          </KpiItem>
          <KpiItem>
            <KpiLabel>당월 납부된 수수료 총액</KpiLabel>
            <KpiValue>{fmt(paidTotal)}</KpiValue>
          </KpiItem>
        </KpiGrid>
      </SummaryCard>

      {/* 3탭 */}
      <TabRow>
        {TABS.map((t) => (
          <TabBtn key={t.key} type="button" $active={tab === t.key} onClick={() => setTab(t.key)}>
            {t.label}
          </TabBtn>
        ))}
      </TabRow>

      {/* 탭 콘텐츠 */}
      {loading ? (
        <EmptyCard><EmptyText>불러오는 중...</EmptyText></EmptyCard>
      ) : data.length > 0 ? (
        <ListWrap>
          {tab === 'receivable' && (
            <ReceivableSummary>
              <span>미납 합계: {fmt(receivableTotal)}</span>
              <ReceivablePayBtn type="button" onClick={handlePayReceivables} disabled={paying || receivables.length === 0}>
                {paying ? '결제창 여는 중…' : '결제하기 →'}
              </ReceivablePayBtn>
            </ReceivableSummary>
          )}
          {groupByMonth(data).map((group) => (
            <MonthGroup key={group.label}>
              <MonthLabel>{group.label} ({group.items.length}건)</MonthLabel>
              {group.items.map((d) => {
                const isAdminReq = d.source === 'admin_request';
                const headLabel = isAdminReq
                  ? (d.productName || d.label || '본사 청구')
                  : ((d.label === '예약완료 미수금' ? '계약완료 수수료' : d.label) || '-');
                return (
                <ListCard key={d.id}>
                  <ListTop>
                    <ListTopLeft>
                      <ListBadge $status={d.status}>{d.statusLabel || d.status}</ListBadge>
                      <ListDesc>{headLabel}{d.method && !isAdminReq ? ` · ${d.method}` : ''}</ListDesc>
                    </ListTopLeft>
                    <ListAmount>{fmt(d.amount)}</ListAmount>
                  </ListTop>
                  <ListBottom>
                    <ListDate>{d.date}</ListDate>
                    <DetailToggle type="button" onClick={() => toggleExpand(d.id)}>
                      {expanded[d.id] ? '접기' : '상세보기'}
                      {expanded[d.id] ? <IoChevronUpOutline size={14} /> : <IoChevronDownOutline size={14} />}
                    </DetailToggle>
                  </ListBottom>
                  {expanded[d.id] && (
                    <DetailBox>
                      {isAdminReq ? (
                        <>
                          <DetailRow><DetailLabel>상품명</DetailLabel><DetailValue>{d.productName || '-'}</DetailValue></DetailRow>
                          <DetailRow><DetailLabel>메모</DetailLabel><DetailValue>{d.memo || '-'}</DetailValue></DetailRow>
                          <DetailRow><DetailLabel>가격</DetailLabel><DetailValue>{fmt(d.amount)}</DetailValue></DetailRow>
                          {d.status === 'pending' && (
                            <AdminReqActions>
                              <CancelBtn type="button" onClick={() => handleCancelAdminRequest(d.id)}>취소하기</CancelBtn>
                              <PayNowBtn type="button" onClick={() => handlePayAdminRequest(d)}>결제하기</PayNowBtn>
                            </AdminReqActions>
                          )}
                        </>
                      ) : (
                        <>
                          <DetailRow><DetailLabel>상품유형</DetailLabel><DetailValue>{d.categoryLabel || '-'}</DetailValue></DetailRow>
                          <DetailRow><DetailLabel>청구가격</DetailLabel><DetailValue>{fmt(d.amount)}</DetailValue></DetailRow>
                          <DetailDivider />
                          <DetailSubTitle>청구 / 결제 정보</DetailSubTitle>
                          <DetailRow>
                            <DetailLabel>서비스 명</DetailLabel>
                            <DetailValue>{d.serviceName || (d.label === '예약완료 미수금' ? '계약완료 수수료' : d.label) || '-'}</DetailValue>
                          </DetailRow>
                          <DetailRow>
                            <DetailLabel>상품 ID</DetailLabel>
                            <DetailValue>{d.productId || `PAY-${String(d.id || '').slice(-4).toUpperCase()}`}</DetailValue>
                          </DetailRow>
                        </>
                      )}
                    </DetailBox>
                  )}
                </ListCard>
                );
              })}
            </MonthGroup>
          ))}
        </ListWrap>
      ) : (
        <EmptyCard>
          <IoBanOutline size={40} color="#D1D5DB" />
          <EmptyTitle>{emptyInfo.title}</EmptyTitle>
          {emptyInfo.sub && <EmptySub>{emptyInfo.sub}</EmptySub>}
        </EmptyCard>
      )}

      {/* 정산 안내 */}
      <NoticeBox>
        <NoticeIcon>⊞</NoticeIcon>
        <NoticeContent>
          <NoticeTitle>정산 안내</NoticeTitle>
          {NOTICES.map((n, i) => <NoticeLine key={i}>· {n}</NoticeLine>)}
        </NoticeContent>
      </NoticeBox>
    </Wrap>
  );
};

export default SettlementManage;

// ───── 스타일 ─────
const Wrap = styled.div`
  max-width: 680px;
  @media (min-width: 1024px) { margin: 0 auto; }
`;

const PageHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  margin-bottom: 20px;
`;

const PageTitle = styled.h1`
  font-size: 22px;
  font-weight: 800;
  color: ${({ theme }) => theme.colors.text};
  margin: 0 0 4px;
`;

const PageSub = styled.p`
  font-size: 13px;
  color: ${({ theme }) => theme.colors.textSecondary};
  margin: 0;
`;

const RefreshBtn = styled.button`
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 8px 14px;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: 8px;
  background: #fff;
  font-size: 12px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.textSecondary};
  cursor: pointer;
  white-space: nowrap;
  flex-shrink: 0;
  &:hover { background: #F9FAFB; }
`;

const SummaryCard = styled.div`
  background: #F1F5FF;
  border-radius: 16px;
  padding: 24px 20px;
  margin-bottom: 20px;
  overflow: hidden;
`;

const SummaryTop = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
`;

const SummaryInfo = styled.div``;

const SummaryLabel = styled.p`
  font-size: 12px;
  color: #6B7280;
  margin: 0 0 4px;
`;

const SummaryAmount = styled.p`
  font-size: 32px;
  font-weight: 800;
  color: ${({ theme }) => theme.colors.text};
  margin: 0;
`;

const PayAllBtn = styled.button`
  padding: 10px 20px;
  background: ${({ theme }) => theme.colors.primary};
  color: #fff;
  border: none;
  border-radius: 10px;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
  white-space: nowrap;
  &:hover { opacity: 0.9; }
  &:disabled { opacity: 0.5; cursor: not-allowed; }
`;

const KpiGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
`;

const KpiItem = styled.div`
  background: #fff;
  border-radius: 12px;
  padding: 12px 14px;
`;

const KpiLabel = styled.p`
  font-size: 11px;
  color: #9CA3AF;
  margin: 0 0 4px;
`;

const KpiValue = styled.p`
  font-size: 18px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.text};
  margin: 0;
`;

const TabRow = styled.div`
  display: flex;
  justify-content: center;
  gap: 0;
  border-bottom: 1px solid ${({ theme }) => theme.colors.borderLight};
  margin-bottom: 20px;
`;

const TabBtn = styled.button`
  padding: 12px 24px;
  font-size: 14px;
  font-weight: ${({ $active }) => $active ? 700 : 500};
  color: ${({ $active, theme }) => $active ? theme.colors.text : theme.colors.textSecondary};
  background: none;
  border: none;
  border-bottom: 2px solid ${({ $active, theme }) => $active ? theme.colors.text : 'transparent'};
  margin-bottom: -1px;
  cursor: pointer;
  white-space: nowrap;
`;

const ListWrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-bottom: 24px;
`;

const ReceivableSummary = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: 14px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.text};
  padding-bottom: 8px;
`;

const ReceivablePayBtn = styled.button`
  padding: 6px 14px;
  background: none;
  border: none;
  color: ${({ theme }) => theme.colors.primary};
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  &:disabled { opacity: 0.5; cursor: not-allowed; }
`;

const ListCard = styled.div`
  background: #fff;
  border: 1px solid ${({ theme }) => theme.colors.borderLight};
  border-radius: 12px;
  padding: 16px;
`;

const ListTop = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 8px;
`;

const ListTopLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1;
  min-width: 0;
`;

const MonthGroup = styled.div`
  margin-bottom: 16px;
  display: flex;
  flex-direction: column;
  gap: 14px;
`;

const MonthLabel = styled.p`
  font-size: 13px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.textSecondary};
  margin: 0 0 10px;
`;

const DetailToggle = styled.button`
  display: flex;
  align-items: center;
  gap: 4px;
  background: none;
  border: none;
  font-size: 12px;
  color: ${({ theme }) => theme.colors.textSecondary};
  cursor: pointer;
  padding: 0;
  &:hover { color: ${({ theme }) => theme.colors.primary}; }
`;

const DetailBox = styled.div`
  margin-top: 12px;
  padding: 14px;
  background: #F9FAFB;
  border-radius: 10px;
  border: 1px solid ${({ theme }) => theme.colors.borderLight};
`;

const DetailDivider = styled.div`
  height: 1px;
  background: ${({ theme }) => theme.colors.borderLight};
  margin: 10px 0;
`;

const DetailSubTitle = styled.p`
  font-size: 12px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.text};
  margin: 0 0 8px;
`;

const DetailMuted = styled.p`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.textLight};
  margin: 0;
`;

const statusColors = {
  expected: '#22C55E',
  completed: '#4A6CF7',
  confirming: '#F59E0B',
  pending: '#F59E0B',
  rejected: '#EF4444',
  unpaid: '#EF4444',
};

const ListBadge = styled.span`
  padding: 3px 10px;
  border-radius: 6px;
  font-size: 11px;
  font-weight: 600;
  color: ${({ $status }) => statusColors[$status] || '#9CA3AF'};
  background: #fff;
  border: 1px solid ${({ $status }) => statusColors[$status] || '#9CA3AF'};
  white-space: nowrap;
`;

const ListDesc = styled.span`
  flex: 1;
  font-size: 14px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const ListAmount = styled.span`
  font-size: 15px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.text};
  flex-shrink: 0;
`;

const ListBottom = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const ListDate = styled.span`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.textSecondary};
`;

const ExpandBtn = styled.button`
  background: none;
  border: none;
  font-size: 12px;
  color: ${({ theme }) => theme.colors.textSecondary};
  cursor: pointer;
`;

const DetailSection = styled.div`
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1px solid ${({ theme }) => theme.colors.borderLight};
`;

const DetailRow = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 4px 0;
`;

const DetailLabel = styled.span`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.textSecondary};
`;

const DetailValue = styled.span`
  font-size: 12px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text};
`;

const EmptyCard = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  padding: 48px 24px;
  background: #F9FAFB;
  border-radius: 12px;
  margin-bottom: 24px;
`;

const EmptyText = styled.p`
  font-size: 14px;
  color: ${({ theme }) => theme.colors.textSecondary};
`;

const EmptyTitle = styled.p`
  font-size: 15px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.text};
  margin: 4px 0 0;
`;

const EmptySub = styled.p`
  font-size: 13px;
  color: ${({ theme }) => theme.colors.textSecondary};
  margin: 0;
`;

const NoticeBox = styled.div`
  display: flex;
  gap: 10px;
  background: #F1F5FF;
  border-radius: 12px;
  padding: 16px 20px;
`;

const NoticeIcon = styled.span`
  font-size: 18px;
  color: ${({ theme }) => theme.colors.primary};
  flex-shrink: 0;
  line-height: 1.4;
`;

const NoticeContent = styled.div``;

const NoticeTitle = styled.p`
  font-size: 13px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.primary};
  margin: 0 0 6px;
`;

const AdminReqActions = styled.div`
  display: flex;
  gap: 8px;
  margin-top: 12px;
`;

const PayNowBtn = styled.button`
  flex: 1;
  height: 40px;
  border: none;
  border-radius: 8px;
  background: ${({ theme }) => theme.colors.primary};
  color: #fff;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
  &:hover { opacity: 0.9; }
`;

const CancelBtn = styled.button`
  flex: 1;
  height: 40px;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: 8px;
  background: #fff;
  color: ${({ theme }) => theme.colors.textSecondary};
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  &:hover { background: #F9FAFB; }
`;

const NoticeLine = styled.p`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.textSecondary};
  line-height: 1.7;
  margin: 0;
`;
